import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ROUTES } from "../Routes";
import { Footer, PartnerLogos } from "../components";
import { Language } from "../constants";
import { useA11y, useStrings } from "../i18n";

interface MarkdownPageProps {
  file: string;
  lang: Language;
}

const markdownComponents = {
  a: ({ node, children, ...props }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

const creditsComponents = {
  ...markdownComponents,
  h3: ({ node, children, ...props }: React.ComponentPropsWithoutRef<"h2"> & { node?: unknown }) => (
    <h2 {...props}>{children}</h2>
  ),
};

function CreditsContent({ content, title }: { content: string; title: string }) {
  const codeEtcMarker = "# Code Etc";
  const codeEtcIdx = content.indexOf(codeEtcMarker);
  const beforeCodeEtcLogo = codeEtcIdx === -1 ? content : content.slice(0, codeEtcIdx);
  const afterCodeEtcLogo = codeEtcIdx === -1 ? "" : content.slice(codeEtcIdx + codeEtcMarker.length);

  return (
    <div className="prose prose-stone prose-sm max-w-none prose-hr:border-stone-300">
      <h1 className="sr-only">{title}</h1>
      <ReactMarkdown components={creditsComponents}>{beforeCodeEtcLogo}</ReactMarkdown>
      <img src="/logos/code_etc_logo.svg" alt="Code Etc" className="not-prose h-16 w-auto my-2 -ml-3" />
      <ReactMarkdown components={creditsComponents}>{afterCodeEtcLogo}</ReactMarkdown>
    </div>
  );
}

export function MarkdownPage({ file, lang }: MarkdownPageProps) {
  const navigate = useNavigate();
  const Strings = useStrings(lang);
  const A11y = useA11y(lang);

  useEffect(() => {
    const titles: Record<string, string> = {
      about: Strings.aboutTitle,
      terms: Strings.termsTitle,
      credits: Strings.creditsTitle,
    };
    document.title = titles[file] ? `${titles[file]} — ${Strings.pageTitle}` : Strings.pageTitle;
  }, [file, Strings]);
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const errorContent = `# ${Strings.error}\n\n${Strings.loadFailed}`;

    // Load language-specific file (e.g., about_el.md) or fallback to default (about.md)
    const langSuffix = lang === 'EN' ? '' : `_${lang.toLowerCase()}`;
    const localizedFile = `${file}${langSuffix}`;

    fetch(`/content/${localizedFile}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        // Fallback to English if localized version doesn't exist
        if (lang !== 'EN') {
          fetch(`/content/${file}.md`)
            .then((res) => res.text())
            .then((text) => {
              setContent(text);
              setLoading(false);
            })
            .catch(() => {
              setContent(errorContent);
              setLoading(false);
            });
        } else {
          setContent(errorContent);
          setLoading(false);
        }
      });
  }, [file, lang, Strings]);

  return (
    <div className="min-h-dvh flex flex-col bg-stone-50">
      <header className="px-6 pt-4 pb-2">
        <button
          onClick={() => navigate(ROUTES.HOME)}
          className="text-sm text-stone-600 hover:text-stone-900 flex items-center gap-1 py-1.5 min-h-6"
        >
          <span className="font-mono" aria-hidden="true">←</span>
          <span>{Strings.back}</span>
        </button>
      </header>
      <main className="flex-1 px-6 pb-4">
        <div role="status" aria-live="polite">
          {loading && <p className="text-stone-600">{Strings.loading}</p>}
          {!loading && <p className="sr-only">{A11y.contentLoaded}</p>}
        </div>
        {!loading && (
          <>
            {file === "credits" ? (
              <CreditsContent content={content} title={Strings.creditsTitle} />
            ) : (
              <div className="prose prose-stone prose-sm max-w-none prose-hr:border-stone-300">
                <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
              </div>
            )}
            {file === "credits" && (
              <div className="mt-10 pt-6 border-t border-stone-200">
                <PartnerLogos lang={lang} />
              </div>
            )}
          </>
        )}
      </main>
      <Footer lang={lang} />
    </div>
  );
}
